import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import AdminLayout from '../components/AdminLayout';

const API = process.env.REACT_APP_API_URL || 'http://localhost:3001';

// Solid pills — saturated hue as background, white text (was washed-out
// light-tint pills). Keeps each status's semantic color.
const STATUS_COLORS = {
  pending: { bg: '#c2410c', color: '#ffffff', label: 'Pending' },
  reviewing: { bg: '#a16207', color: '#ffffff', label: 'Reviewing' },
  offer_submitted: { bg: '#15803d', color: '#ffffff', label: 'Offer Submitted' },
  accepted: { bg: '#15803d', color: '#ffffff', label: 'Accepted' },
  cancelled: { bg: '#ff082f', color: '#ffffff', label: 'Cancelled' },
  completed: { bg: '#3a6ad9', color: '#ffffff', label: 'Completed' },
  // Legacy fallback — historical rows may still have status='closed'
  // in the DB. The backend derives a fresh status off the loan stage
  // for every response, so this only matters if a raw value sneaks
  // through. Rendered identically to the new 'completed' label.
  closed:    { bg: '#3a6ad9', color: '#ffffff', label: 'Completed' },
};

const fmt = (n) => n != null ? '$' + Number(n).toLocaleString() : '—';

export default function AdminOfferRequests() {
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [editMap, setEditMap] = useState({});
  const [savingId, setSavingId] = useState(null);

  // Multi-select status filter mirroring AdminShowingRequests. Set
  // semantics: empty Set means "no status constraint, show everything"
  // (useful for catching the long-tail closed/cancelled rows); a
  // populated Set filters to only the chosen statuses. Default
  // pre-filters to the three "active" statuses so the desk lands on a
  // working queue and has to opt-in to see terminal rows.
  const DEFAULT_STATUSES = ['pending', 'reviewing', 'offer_submitted'];
  const [filterStatuses, setFilterStatuses] = useState(
    () => new Set(DEFAULT_STATUSES)
  );
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStartDate, setFilterStartDate] = useState('');
  const [filterEndDate, setFilterEndDate] = useState('');

  const headers = { Authorization: `Bearer ${localStorage.getItem('doorly_admin_token')}` };

  const fetchRequests = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/admin/borrowers/offer-requests`, { headers });
      setRequests(res.data.requests || []);
    } catch {}
    setLoading(false);
  }, []); // eslint-disable-line

  useEffect(() => { fetchRequests(); }, [fetchRequests]);

  const toggleExpand = (id) => {
    const wasOpen = expandedId === id;
    setExpandedId((prev) => (prev === id ? null : id));
    setEditMap((prev) => {
      if (prev[id]) return prev;
      const req = requests.find((r) => r.id === id);
      if (!req) return prev;
      return {
        ...prev,
        [id]: {
          admin_notes: req.admin_notes || '',
        },
      };
    });
    // Mark the row read on first open so the left-nav badge clears. Safe to
    // fire repeatedly; backend UPDATE is idempotent. Only fire on the open
    // transition (skip when collapsing) so we don't churn the API.
    if (!wasOpen) {
      const req = requests.find((r) => r.id === id);
      if (req && !req.is_read) {
        axios.post(`${API}/api/admin/borrowers/offer-requests/${id}/mark-read`, {}, { headers })
          .then(() => setRequests((prev) => prev.map((r) => r.id === id ? { ...r, is_read: true } : r)))
          .catch(() => { /* non-fatal — badge will catch up on next refresh */ });
      }
    }
  };

  const handleSave = async (id) => {
    setSavingId(id);
    try {
      await axios.patch(`${API}/api/admin/borrowers/offer-requests/${id}`, editMap[id], { headers });
      await fetchRequests();
    } catch {}
    setSavingId(null);
  };

  const pendingCount = requests.filter((r) => r.status === 'pending').length;

  const filtered = requests.filter((r) => {
    // Empty Set = no status constraint (show all rows). Otherwise
    // the row's status must be in the selected Set.
    if (filterStatuses.size > 0 && !filterStatuses.has(r.status)) return false;
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      const matchName = `${r.first_name || ''} ${r.last_name || ''}`.toLowerCase().includes(q);
      const matchLoan = (r.loan_number || '').toLowerCase().includes(q);
      const matchAddress = (r.address || '').toLowerCase().includes(q);
      const matchMLS = (r.mls_number || '').toLowerCase().includes(q);
      if (!matchName && !matchLoan && !matchAddress && !matchMLS) return false;
    }
    if (filterStartDate && new Date(r.created_at) < new Date(filterStartDate + 'T00:00:00')) return false;
    if (filterEndDate && new Date(r.created_at) > new Date(filterEndDate + 'T23:59:59')) return false;
    return true;
  });

  // "Default" status filter = the 3-element pre-filtered Set. Anything
  // else (different size, OR same size with different membership)
  // counts as an active filter — so the Clear button surfaces and the
  // user can snap back to the default queue. Match the showings-page
  // pattern by checking BOTH size and full-membership equality, not
  // just size (a 1-add/1-remove swap is still a non-default filter).
  const isDefaultStatusSet = filterStatuses.size === DEFAULT_STATUSES.length
    && DEFAULT_STATUSES.every((s) => filterStatuses.has(s));
  const hasFilters = !isDefaultStatusSet || searchQuery || filterStartDate || filterEndDate;
  const clearFilters = () => {
    setSearchQuery('');
    setFilterStartDate('');
    setFilterEndDate('');
    setFilterStatuses(new Set(DEFAULT_STATUSES));
  };

  return (
    <AdminLayout>
      <div style={{ padding: '24px 24px 40px', maxWidth: '1100px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
          <h1 style={{ fontSize: '20px', fontWeight: '700', color: '#111827', margin: 0 }}>Offer Requests</h1>
          {pendingCount > 0 && (
            <span style={{ backgroundColor: '#ff082f', color: '#fff', borderRadius: '9999px', fontSize: '11px', fontWeight: '700', padding: '2px 8px' }}>
              {pendingCount} pending
            </span>
          )}
        </div>

        {/* Filter Bar */}
        <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '12px', padding: '16px 20px', marginBottom: '16px', display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div style={{ flex: '1', minWidth: '200px' }}>
            <label style={{ fontSize: '11px', color: '#6b7280', fontWeight: '600', display: 'block', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Search</label>
            <div style={{ position: 'relative' }}>
              <span style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', fontSize: '14px', color: '#9ca3af' }}>🔍</span>
              <input
                type="text"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                placeholder="Borrower name, loan #, address, MLS#..."
                style={{ width: '100%', padding: '8px 10px 8px 32px', border: '1px solid #e5e7eb', borderRadius: '8px', fontSize: '13px', outline: 'none', boxSizing: 'border-box' }}
              />
            </div>
          </div>
          {/* Status pills — multi-select, mirroring Showing Requests.
              flexBasis: 100% breaks the pill row onto its own line so
              it sits below Search and the date inputs land beneath
              it. Each pill is its own button; clicking toggles
              membership in filterStatuses. Empty Set surfaces a
              "Showing all statuses" hint instead of leaving the row
              empty/ambiguous. */}
          <div style={{
            display: 'flex', flexWrap: 'wrap',
            gap: 6, flexBasis: '100%',
          }}>
            <span style={{
              fontSize: 11, color: '#9ca3af',
              fontWeight: 600, textTransform: 'uppercase',
              letterSpacing: '0.05em',
              alignSelf: 'center', marginRight: 4,
            }}>
              Status:
            </span>

            {filterStatuses.size === 0 && (
              <span style={{
                fontSize: 11, color: '#9ca3af',
                fontStyle: 'italic', alignSelf: 'center',
              }}>
                Showing all statuses
              </span>
            )}

            {[
              { value: 'pending',          label: 'Pending',
                bg: '#c2410c', text: '#ffffff', border: '#c2410c' },
              { value: 'reviewing',        label: 'Reviewing',
                bg: '#a16207', text: '#ffffff', border: '#a16207' },
              { value: 'offer_submitted',  label: 'Offer Submitted',
                bg: '#15803d', text: '#ffffff', border: '#15803d' },
              { value: 'accepted',         label: 'Accepted',
                bg: '#15803d', text: '#ffffff', border: '#15803d' },
              { value: 'cancelled',        label: 'Cancelled',
                bg: '#ff082f', text: '#ffffff', border: '#ff082f' },
              { value: 'completed',        label: 'Completed',
                bg: '#3a6ad9', text: '#ffffff', border: '#3a6ad9' },
            ].map(s => {
              const active = filterStatuses.has(s.value);
              return (
                <button
                  key={s.value}
                  onClick={() => {
                    setFilterStatuses(prev => {
                      const next = new Set(prev);
                      if (next.has(s.value)) next.delete(s.value);
                      else next.add(s.value);
                      return next;
                    });
                  }}
                  style={{
                    fontSize: 11, fontWeight: 600,
                    padding: '3px 10px',
                    borderRadius: 20,
                    border: `1px solid ${active
                      ? s.border : '#e5e7eb'}`,
                    background: active ? s.bg : '#ffffff',
                    color: active ? s.text : '#9ca3af',
                    cursor: 'pointer',
                    transition: 'all 0.15s',
                  }}
                >
                  {s.label}
                </button>
              );
            })}
          </div>
          <div>
            <label style={{ fontSize: '11px', color: '#6b7280', fontWeight: '600', display: 'block', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>From</label>
            <input type="date" value={filterStartDate} onChange={e => setFilterStartDate(e.target.value)}
              style={{ padding: '8px 10px', border: '1px solid #e5e7eb', borderRadius: '8px', fontSize: '13px', outline: 'none', cursor: 'pointer' }} />
          </div>
          <div>
            <label style={{ fontSize: '11px', color: '#6b7280', fontWeight: '600', display: 'block', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>To</label>
            <input type="date" value={filterEndDate} onChange={e => setFilterEndDate(e.target.value)}
              style={{ padding: '8px 10px', border: '1px solid #e5e7eb', borderRadius: '8px', fontSize: '13px', outline: 'none', cursor: 'pointer' }} />
          </div>
          {hasFilters && (
            <button onClick={clearFilters}
              style={{ padding: '8px 14px', background: '#f3f4f6', color: '#374151', border: '1px solid #e5e7eb', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', display: 'inline-flex', alignItems: 'center', gap: '4px', alignSelf: 'flex-end' }}>
              ✕ Clear
            </button>
          )}
          <div style={{ alignSelf: 'flex-end', marginLeft: 'auto' }}>
            <span style={{ fontSize: '13px', color: '#6b7280' }}>
              Showing <strong style={{ color: '#111827' }}>{filtered.length}</strong> of {requests.length}
            </span>
          </div>
        </div>

        {loading ? (
          <p style={{ color: '#6b7280', fontSize: '14px' }}>Loading...</p>
        ) : filtered.length === 0 ? (
          <div style={{ backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '40px', textAlign: 'center', color: '#6b7280', fontSize: '14px' }}>
            {requests.length === 0 ? 'No offer requests yet.' : 'No requests match your filters.'}
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {/* Column header row — mirrors the collapsed-card grid
                track-for-track so labels stay aligned with the row
                values below. Uppercase / muted styling keeps it from
                competing visually with the card content. */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr 140px 90px 120px 90px',
              gap: '12px',
              alignItems: 'center',
              padding: '0 20px',
              fontSize: '10px',
              fontWeight: 700,
              color: '#9ca3af',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
            }}>
              <span>Borrower</span>
              <span>Address</span>
              <span>List Price</span>
              <span style={{ textAlign: 'center' }}>Fee</span>
              <span style={{ textAlign: 'center' }}>Status</span>
              <span style={{ textAlign: 'right' }}>Submitted</span>
            </div>
            {filtered.map((req) => {
              const s = STATUS_COLORS[req.status] || STATUS_COLORS.pending;
              const isOpen = expandedId === req.id;
              const edit = editMap[req.id] || {};

              return (
                <div key={req.id} style={{ backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px', overflow: 'hidden' }}>
                  <button
                    onClick={() => toggleExpand(req.id)}
                    style={{ width: '100%', display: 'grid', gridTemplateColumns: '1fr 1fr 140px 90px 120px 90px', gap: '12px', alignItems: 'center', padding: '14px 20px', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left' }}
                  >
                    <span style={{ fontSize: '13px', fontWeight: '600', color: '#111827' }}>
                      {[req.first_name, req.last_name].filter(Boolean).join(' ') || 'Unknown Borrower'}
                    </span>
                    <span style={{ fontSize: '13px', color: '#374151', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {req.address || '—'}
                    </span>
                    {/* Listing price — borrower-entered on the offer
                        request form (Homes.js submitPropertyForm.list_price).
                        Replaces the prior column that surfaced
                        users.doorly_purchase_price (the loan-side max
                        approval); on this page the listing price is
                        the more useful signal because it's the number
                        the borrower is actually trying to offer on. */}
                    <span style={{ fontSize: '12px', color: '#6b7280' }}>
                      {req.list_price ? fmt(req.list_price) : '—'}
                    </span>
                    <span style={{ fontSize: '11px', fontWeight: '700', textAlign: 'center' }}>
                      {req.offer_initiation_fee_paid ? (
                        <span style={{ background: '#acf44e', color: '#1a1a1a', padding: '2px 8px', borderRadius: '4px' }}>$500 Paid</span>
                      ) : req.offer_initiation_payment_method === 'waived' ? (
                        <span style={{ background: '#edf1fb', color: '#3a6ad9', padding: '2px 8px', borderRadius: '4px' }} title="Borrower already paid the $500 fee on a prior offer">Waived</span>
                      ) : (
                        <span style={{ background: '#fff0f3', color: '#ff082f', padding: '2px 8px', borderRadius: '4px' }}>Pending</span>
                      )}
                    </span>
                    <span style={{ fontSize: '11px', fontWeight: '600', padding: '2px 8px', borderRadius: '9999px', backgroundColor: s.bg, color: s.color, textAlign: 'center' }}>
                      {s.label}
                    </span>
                    <span style={{ fontSize: '11px', color: '#9ca3af', textAlign: 'right' }}>
                      {new Date(req.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    </span>
                  </button>

                  {isOpen && (
                    <div style={{ borderTop: '1px solid #f3f4f6', padding: '16px 20px', backgroundColor: '#fafafa' }}>
                      {/* Contact cards — Borrower (left) + Listing Agent
                          (right). Listing agent fields are populated
                          from SimplyRETS at offer-request submit time
                          (homes.js writes orq.listing_agent_*); fall
                          back to "—" when MLS lookup didn't resolve
                          a match. */}
                      <div className="admin-two-col-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
                        <div style={contactCardStyle}>
                          <p style={cardTitleStyle}>Borrower</p>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                            <div>
                              <p style={labelStyle}>Name</p>
                              <p style={valueStyle}>
                                {[req.first_name, req.last_name].filter(Boolean).join(' ') || '—'}
                              </p>
                            </div>
                            <div>
                              <p style={labelStyle}>Email</p>
                              <p style={valueStyle}>{req.email || '—'}</p>
                            </div>
                            <div>
                              <p style={labelStyle}>Phone</p>
                              <p style={valueStyle}>{req.phone || '—'}</p>
                            </div>
                          </div>
                        </div>
                        <div style={contactCardStyle}>
                          <p style={cardTitleStyle}>Listing Agent</p>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                            <div>
                              <p style={labelStyle}>Name</p>
                              <p style={valueStyle}>{req.listing_agent_name || '—'}</p>
                            </div>
                            <div>
                              <p style={labelStyle}>Email</p>
                              <p style={valueStyle}>{req.listing_agent_email || '—'}</p>
                            </div>
                            <div>
                              <p style={labelStyle}>Phone</p>
                              <p style={valueStyle}>{req.listing_agent_phone || '—'}</p>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="admin-two-col-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                        <div>
                          <p style={labelStyle}>Property Address</p>
                          <p style={valueStyle}>{req.address || '—'}</p>
                        </div>
                        <div>
                          <p style={labelStyle}>Initiation Fee</p>
                          <p style={valueStyle}>
                            {req.offer_initiation_fee_paid
                              ? `✓ $500 Paid via ${req.offer_initiation_payment_method || 'Plaid'}`
                              : req.offer_initiation_payment_method === 'waived'
                                ? '✓ Waived — borrower already paid on a prior offer request'
                                : '⚠️ Not yet collected'}
                          </p>
                        </div>
                        {req.notes && (
                          <div style={{ gridColumn: '1 / -1' }}>
                            <p style={labelStyle}>Borrower Notes</p>
                            <p style={valueStyle}>{req.notes}</p>
                          </div>
                        )}
                        {req.reviewed_by && (
                          <div>
                            <p style={labelStyle}>Reviewed By</p>
                            <p style={valueStyle}>{req.reviewed_by}</p>
                          </div>
                        )}
                      </div>

                      {/* Status is read-only here — driven by the
                          offer-workflow handlers (manual-accept-offer,
                          BoldSign webhooks, etc) elsewhere in admin.js.
                          The admin's only editable surface on this row
                          is Admin Notes. */}
                      <div style={{ display: 'grid', gridTemplateColumns: '180px 1fr', gap: '12px', alignItems: 'start', marginBottom: '12px' }}>
                        <div>
                          <p style={labelStyle}>Status</p>
                          <span style={{
                            display: 'inline-block',
                            fontSize: '12px', fontWeight: 600,
                            padding: '4px 12px', borderRadius: '9999px',
                            backgroundColor: s.bg, color: s.color,
                          }}>
                            {s.label}
                          </span>
                        </div>
                        <div>
                          <p style={labelStyle}>Admin Notes</p>
                          <textarea
                            value={edit.admin_notes || ''}
                            onChange={(e) => setEditMap((prev) => ({ ...prev, [req.id]: { ...prev[req.id], admin_notes: e.target.value } }))}
                            rows={2}
                            placeholder="Internal notes..."
                            style={{ ...inputStyle, resize: 'vertical', minHeight: '60px' }}
                          />
                        </div>
                      </div>

                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button
                          onClick={() => handleSave(req.id)}
                          disabled={savingId === req.id}
                          style={{ padding: '7px 16px', backgroundColor: '#3a6ad9', color: '#fff', border: 'none', borderRadius: '6px', fontSize: '13px', fontWeight: '600', cursor: 'pointer' }}
                        >
                          {savingId === req.id ? 'Saving...' : 'Save'}
                        </button>
                        <button
                          onClick={() => navigate(`/admin/borrowers/${req.user_id}?tab=property_details`)}
                          style={{ padding: '7px 16px', backgroundColor: '#acf44e', color: '#1a1a1a', border: 'none', borderRadius: '6px', fontSize: '13px', fontWeight: '600', cursor: 'pointer' }}
                        >
                          Complete Property Module →
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}

const labelStyle = { fontSize: '11px', fontWeight: '600', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em', margin: '0 0 2px' };
const valueStyle = { fontSize: '13px', color: '#111827', margin: 0 };
const inputStyle = { width: '100%', padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px', color: '#111827', backgroundColor: '#fff', boxSizing: 'border-box' };
const contactCardStyle = { background: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '12px 14px' };
const cardTitleStyle = { fontSize: '11px', fontWeight: '700', color: '#374151', textTransform: 'uppercase', letterSpacing: '0.05em', margin: '0 0 10px', paddingBottom: '8px', borderBottom: '1px solid #f3f4f6' };
